import { FC, MouseEvent } from 'react';

import { HIGHER_GRADES } from 'src/constants/grades.constants';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import {
  AssignmentsViewOptions,
  DesmosAssignment,
  SingleAssignment,
} from 'src/interfaces/assignment.types';
import { TaughtLessonAssignment } from 'src/interfaces/class-stats.types';
import { GRADES } from 'src/interfaces/grades.types';
import {
  getAssignmentDomain,
  getAssignmentThumbnailPath,
  getIsAssignmentDueDateExpired,
  isDesmosAssignmentContent,
} from 'src/utils/assignments.utils';

import { HigherGradeCardContent } from './higher-grade-card-content';
import { LowerGradeCardContent } from './lower-grade-card-content';
import { MiddleSchoolCardContent } from './middle-school-card-content';

import './stream-card-content.scss';

interface Props {
  assignment: SingleAssignment | TaughtLessonAssignment | DesmosAssignment;
  grade: GRADES;
  onMsButtonClick?: (e: MouseEvent<HTMLElement>) => void;
  msButtonHref?: string;
  isStatusChangeDialogVisible?: boolean;
  onUndoButtonClick?: (e: MouseEvent<HTMLElement>) => void;
  isCardRemovalAnimationInProgress?: boolean;
  k5Section?: AssignmentsViewOptions.TODO | AssignmentsViewOptions.PAST_WORK;
}

export const StreamCardContent: FC<Props> = ({
  assignment,
  grade,
  onMsButtonClick,
  msButtonHref,
  isStatusChangeDialogVisible,
  onUndoButtonClick,
  isCardRemovalAnimationInProgress,
  k5Section,
}) => {
  const isMiddleSchoolView = useIsMiddleSchoolView();
  const isHigherGradeUser = HIGHER_GRADES.includes(grade);

  const content = assignment.content;

  const isCustomAssignment = Boolean(
    'customContentId' in content ? content.customContentId : undefined,
  );
  const lessonTitle =
    'lessonTitle' in content ? content.lessonTitle : undefined;
  const title = 'title' in content ? content.title : undefined;
  const domain = 'domain' in content ? content.domain : undefined;
  const unitKey = 'unitKey' in content ? content.unitKey : undefined;
  const subject = 'subject' in content ? content.subject : undefined;
  const isFullyCustomAssignment = Boolean(isCustomAssignment && !unitKey);
  const assignmentTitle = title ?? lessonTitle;
  const assignmentDomain = getAssignmentDomain(domain);
  const thumbnailPath = getAssignmentThumbnailPath(assignment.content);

  const isAssignmentDueDateExpired =
    assignment.type !== 'TaughtLessonAssignment' &&
    getIsAssignmentDueDateExpired(assignment);

  const domainsWithDistortedImages = ['CKLA', 'Caminos'];

  const isThumbnailDistorted = Boolean(
    domain &&
      domainsWithDistortedImages.includes(domain) &&
      !isDesmosAssignmentContent(content),
  );

  const isTaughtLesson = assignment.type === 'TaughtLessonAssignment';

  if (isMiddleSchoolView) {
    return (
      <MiddleSchoolCardContent
        isThumbnailDistorted={isThumbnailDistorted}
        assignment={assignment}
        thumbnailPath={thumbnailPath}
        isFullyCustom={isFullyCustomAssignment}
        isCustomized={isCustomAssignment}
        domain={domain}
        title={assignmentTitle}
        msButtonHref={msButtonHref}
        onMsButtonClick={onMsButtonClick}
      />
    );
  }

  if (isHigherGradeUser) {
    return (
      <HigherGradeCardContent
        assignment={assignment}
        thumbnailPath={thumbnailPath}
        isThumbnailDistorted={isThumbnailDistorted}
        isFullyCustom={isFullyCustomAssignment}
        isCustomized={isCustomAssignment}
        domain={assignmentDomain}
        title={assignmentTitle}
        isTaughtLesson={isTaughtLesson}
        dueDate={assignment.dueDate}
        isOverdue={isAssignmentDueDateExpired}
        isStatusChangeDialogVisible={isStatusChangeDialogVisible}
        onUndoButtonClick={onUndoButtonClick}
        isCardRemovalAnimationInProgress={isCardRemovalAnimationInProgress}
        section={k5Section}
      />
    );
  }

  return (
    <LowerGradeCardContent
      assignment={assignment}
      thumbnailPath={thumbnailPath}
      isThumbnailDistorted={isThumbnailDistorted}
      isFullyCustom={isFullyCustomAssignment}
      isCustomized={isCustomAssignment}
      domain={assignmentDomain}
      subject={subject}
      title={assignmentTitle}
      isTaughtLesson={isTaughtLesson}
      dueDate={assignment.dueDate}
      isCardRemovalAnimationInProgress={isCardRemovalAnimationInProgress}
      isStatusChangeDialogVisible={isStatusChangeDialogVisible}
      section={k5Section}
    />
  );
};
